// a2-27 — a flight's route, between the Year's flight form and the
// `flights.from_iata`/`to_iata` columns, and back out as words.
//
// The form offered "Berlin → elsewhere" and stored the made-up code `OTH`,
// so the card read "BER → OTH". "Elsewhere" is not an airport: the side
// Doug did not name is stored as `null`, and the side he named in the form's
// "Where" field is stored as he typed it ("Lisbon"). The columns already
// hold whatever a forwarded itinerary wrote (`VNO`, `DUB`), so a place name
// in them reads the same way on every card: as the place.
//
// No imports: the root suite tests this file directly.

/** A direction is `${from}|${to}` in airport codes, with `*` for the end that
 *  is somewhere other than home or the second home. Not a fixed union any
 *  more: the second home's airport is the profile's (`NOC` for Doug), so the
 *  value `NOC|BER` exists only for a profile that names it. */
export type Direction = string

/** What the form needs of the profile: the home airport and the second home's
 *  label and airports (the first is the one the form offers). */
export interface RouteHome {
  homeIata: string
  secondHome: { label: string; airports: readonly string[] } | null
  /** What the home airport's place is called — the home region's name
   *  (`homeName` in `regions.ts`; C2.3). Optional because this file is
   *  import-free and the Worker's suite calls it bare; it was the literal
   *  'Berlin' and stays what a caller that does not say reads. */
  homeName?: string
}

const DEFAULT_HOME_NAME = 'Berlin'

/**
 * The form's direction choices. "Ireland → Berlin" and its mirror are offered
 * only for a person whose second home names an airport; everyone else gets the
 * two neutral ones. Labels print the profile's own second-home label.
 */
export function directionsFor({ homeIata, secondHome, homeName }: RouteHome): { value: Direction; label: string }[] {
  const HOME_NAME = homeName?.trim() || DEFAULT_HOME_NAME
  const away = secondHome?.airports[0]
  const label = secondHome?.label.trim() || 'Second home'
  return [
    ...(away ? [
      { value: `${away}|${homeIata}`, label: `${label} → ${HOME_NAME}` },
      { value: `${homeIata}|${away}`, label: `${HOME_NAME} → ${label}` },
    ] : []),
    { value: `${homeIata}|*`, label: `${HOME_NAME} → elsewhere` },
    { value: `*|${homeIata}`, label: `Elsewhere → ${HOME_NAME}` },
  ]
}

/**
 * The direction a new flight form opens on: from the profile's second home to
 * its own airport (`${airports[0]}|${home_iata}`), which for Doug's row is the
 * Ireland → Berlin choice the form used to hard-code. No second home opens on
 * "Berlin → elsewhere", the one neutral choice that exists, rather than on a
 * value the select cannot show (the brief says `''`; an empty direction is not
 * in the select, so it would render as no choice).
 */
export function defaultDirection(homeIata: string, secondHomeAirports: readonly string[]): Direction {
  const away = secondHomeAirports[0]
  return away ? `${away}|${homeIata}` : `${homeIata}|*`
}

/** The code the form used to write for "elsewhere"; read as no place. */
const LEGACY_ELSEWHERE = 'OTH'

const place = (v: string | null | undefined): string | null => {
  const t = (v ?? '').trim()
  return t && t.toUpperCase() !== LEGACY_ELSEWHERE ? t : null
}

/** The columns a form's direction and "Where" field write. */
export function routeFromForm(dir: Direction, where: string): { from_iata: string | null; to_iata: string | null } {
  const [from, to] = dir.split('|')
  const other = place(where)
  return { from_iata: from === '*' ? other : from, to_iata: to === '*' ? other : to }
}

/** The form's direction and "Where" for a stored flight. Anything that is not
 *  home↔second-home is "elsewhere" with its stored place in "Where", so an
 *  emailed BER → VNO opens on a choice that exists and saves back as VNO.
 *  `home` defaults to Berlin's airport and the second home's airport to none
 *  (so nothing is the second home): the caller passes the profile's. */
export function formFromRoute(
  from: string | null | undefined, to: string | null | undefined,
  home: { homeIata?: string; secondAirport?: string | null } = {},
): { dir: Direction; where: string } {
  const h = home.homeIata ?? 'BER'
  const a = home.secondAirport ?? null
  const f = place(from)
  const t = place(to)
  if (a && f === a && t === h) return { dir: `${a}|${h}`, where: '' }
  if (a && f === h && t === a) return { dir: `${h}|${a}`, where: '' }
  if (t === h) return { dir: `*|${h}`, where: f ?? '' }
  return { dir: `${h}|*`, where: t ?? '' }
}

/** "BER → Lisbon", "BER → elsewhere"; nothing when neither end is known. */
export function routeLabel(from: string | null | undefined, to: string | null | undefined): string | undefined {
  const f = place(from)
  const t = place(to)
  if (!f && !t) return undefined
  return `${f ?? 'elsewhere'} → ${t ?? 'elsewhere'}`
}
